import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { event, Identifiable, Project } from "@planzone/dto";
import {
  useListsRetrieval,
  useCreateEvent,
  useAuthentication,
} from "@planzone/view-models";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { Props } from "./types";
import { EventCreationForm } from "./form";
import styles from "./styles.scss";

function EventCreation({
  project,
  list,
  initialFormData,
  range,
  isMultiProject,
  cancel,
}: Props) {
  const filter = { limit: 999999999999, start: 0 };
  const now = new Date();
  const defaultStartTime = new Date(
    initialFormData ? `${initialFormData.startTime}` : now,
  );
  const defaultEndTime = new Date(
    initialFormData ? `${initialFormData.endTime}` : now,
  );
  const { t } = useTranslation();
  const { isRequestPending, error, createEvent } = useCreateEvent();
  const { account } = useAuthentication();
  const [formData, setFormData] = React.useState({
    ...event,
    keyRank: -1,
    ...initialFormData,
    startTime:
      initialFormData && initialFormData.startTime
        ? new Date(
            defaultStartTime.getFullYear(),
            defaultStartTime.getMonth(),
            defaultStartTime.getDate(),
            defaultStartTime.getHours(),
            defaultStartTime.getMinutes(),
          ).toISOString()
        : "",
    endTime:
      initialFormData && initialFormData.endTime
        ? new Date(
            defaultEndTime.getFullYear(),
            defaultEndTime.getMonth(),
            defaultEndTime.getDate(),
            defaultEndTime.getHours(),
            defaultEndTime.getMinutes(),
          ).toISOString()
        : "",
    participants:
      initialFormData?.participants && initialFormData.participants.length > 0
        ? initialFormData.participants
        : account
          ? [account.resource]
          : [],
  });

  const { lists, retrieveLists } = useListsRetrieval(project, filter);
  const [selectedList, setSelectedList] = React.useState(list);
  const [selectAllDay, setSelectAllDay] = useState(false);
  const [selectedProject, setSelectedProject] =
    React.useState<Identifiable<Project> | null>(null);
  const currentProject = selectedProject || project;

  React.useEffect(() => {
    !!currentProject.uuid && retrieveLists({ project, filter });
  }, [currentProject.uuid]);

  return (
    <WorkspaceContext type="project" project={project}>
      <Modal header={t("eventCreation.title")} close={cancel}>
        <form
          className={styles.eventCreation}
          onSubmit={(e) => {
            const startTime = new Date(formData.startTime);
            const endTime = new Date(formData.endTime);
            e.preventDefault();
            !isRequestPending &&
              createEvent({
                project: currentProject,
                account,
                range,
                list: selectedList,
                event: {
                  ...formData,
                  startTime: selectAllDay
                    ? new Date(
                        startTime.getFullYear(),
                        startTime.getMonth(),
                        startTime.getDate(),
                        0,
                        0,
                      ).toISOString()
                    : formData.startTime,
                  endTime: selectAllDay
                    ? new Date(
                        endTime.getFullYear(),
                        endTime.getMonth(),
                        endTime.getDate(),
                        23,
                        59,
                      ).toISOString()
                    : formData.endTime,
                },
              });
          }}
        >
          <div className={styles.main}>
            <div className={styles.form}>
              <EventCreationForm
                formData={formData}
                error={error}
                project={currentProject}
                list={selectedList}
                lists={
                  isMultiProject && selectedProject?.rootList
                    ? [selectedProject.rootList]
                    : [lists]
                }
                isMultiProject={isMultiProject}
                selectAllDay={selectAllDay}
                editForm={setFormData}
                setList={setSelectedList}
                setSelectAllDay={setSelectAllDay}
                onProjectChange={(project) => {
                  setSelectedProject(project);
                  if (project?.rootList) {
                    setSelectedList(project.rootList);
                  }
                }}
              />
            </div>
          </div>
          <div className={styles.buttons}>
            <Button type="cancel" press={cancel}>
              {t("eventCreation.cancel")}
            </Button>
            <Button
              status={isRequestPending ? "pending" : "none"}
              type={"submit"}
              isDisabled={isMultiProject && !selectedProject}
            >
              {t("eventCreation.submit")}
            </Button>
          </div>
        </form>
      </Modal>
    </WorkspaceContext>
  );
}

export { EventCreation };
